/**
 * The shell and the router.
 *
 * NINE SCREENS, NO ROUTER DEPENDENCY. `useRoute` over `pushState`/`popstate`
 * plus regex path matching, all of it thirty lines in app-sidebar.tsx. This app
 * has no server actions, no SSR and no data loaders, so it can have none of
 * react-router's client-side advisories by not having any of the features they
 * are about — and `npm audit` stays at zero for a workspace that ships to a
 * browser holding a live microphone.
 *
 * THE PATH PATTERNS ARE ANCHORED AND ID-SHAPED. `/sessions/x/y` must fall
 * through to Not found rather than fetching a 404 and rendering an error box,
 * and `/agents/log` must not be mistaken for an agent whose id is "log". Both
 * ids are ours and both are opaque, so the pattern is "URL-safe characters and
 * nothing else, to the end of the path".
 *
 * THE GATE IS THREE-STATE, NOT TWO, and that is the whole shape of the auth
 * routing below. `loading` is "we have not asked yet", and it renders a splash;
 * a null user AFTER asking renders the login form. Collapsing them is what makes
 * an app flash a login screen at an already-signed-in person on every reload,
 * once per `/auth/me` round trip, and it is the single most common way this gets
 * written wrong.
 *
 * THE GATE DOES NOT REDIRECT, IT SUBSTITUTES. Somebody who follows a link to one
 * session and has no session of their own gets the login form AT that URL — the
 * address bar is left alone — so signing in re-renders the screen they asked for
 * with nothing to navigate back to and no `?next=` to be tampered with. The one
 * exception is `/login` itself, which for a signed-in person is a screen with
 * nothing on it, so that one does move.
 *
 * FOUR PATHS ARE PUBLIC AND THREE OF THEM RENDER WITHOUT WAITING. A reset or
 * invite link carries its own authorisation in the token and says nothing about
 * the session cookie, so making somebody wait on `/auth/me` before they can use
 * one buys nothing — and a stale cookie must not stop a person accepting an
 * invitation on a shared machine. `/login` is the exception again: it needs to
 * know whether to redirect, so it waits.
 *
 * WHAT IS FETCHED HERE AND WHY IT IS ONLY THE AGENTS. The reference app fetches
 * health and a dashboard up front because a banner and every sidebar badge need
 * them on every screen. Here there is exactly one thing every screen needs: the
 * agent list, because "Start a call" is in the header of all of them and it
 * needs an agent id to point at. The session counts arrive the other way round —
 * the sessions screen already polls the list, so it reports its own totals up
 * through `onCounts` instead of App running a second poller against the same
 * endpoint. The consequence, chosen knowingly: land directly on a session detail
 * page and the nav shows no session count until you visit the list. A missing
 * badge is not an error, and a duplicate poller is a real cost.
 *
 * AND IT IS NOT FETCHED UNTIL THERE IS A USER. Every `/api/*` request without a
 * session is a 401, so an unguarded mount would spend one on the login screen,
 * fail, and — because the banner fires on anything that is not a 4xx from a
 * healthy server — teach the reader to distrust a screen that was working fine.
 * The list is also dropped on sign-out: the next person to sign in may not be
 * allowed to see the same agents.
 *
 * WHY THE AGENT ID IS IN THE QUERY STRING FOR /call. It is the one screen that
 * takes a parameter which is not the thing being displayed, and a reader who
 * bookmarks "call the support agent" should get the support agent. `?agent=` also
 * degrades correctly: with no parameter the first configured agent is used, and
 * with no agents at all the screen says so instead of opening a session against
 * an empty id.
 */
import { useEffect, useState } from 'react'
import { ApiError, agents as agentsApi, type AgentConfig } from '@/api'
import { can } from '@/lib/auth'
import { AuthProvider, useAuth } from '@/components/auth-provider'
import { AppShell } from '@/components/app-shell'
import { AuthSplash } from '@/components/auth-shell'
import { Link, navigate, useRoute, type NavCounts } from '@/components/app-sidebar'

import { SessionsPage } from './pages/Sessions'
import { SessionDetailPage } from './pages/SessionDetail'
import { AgentsPage } from './pages/Agents'
import { AgentEditPage } from './pages/AgentEdit'
import { ToolsPage } from './pages/Tools'
import { ToolEditPage } from './pages/ToolEdit'
import { WebhookEditPage } from './pages/WebhookEdit'
import { WebhooksPage } from './pages/Webhooks'
import { TelephonyPage } from './pages/Telephony'
import { UsersPage } from './pages/Users'
import { AccountPage } from './pages/Account'
// Takes the agent list as a prop rather than fetching it again — see the note on
// what is fetched here, above. It holds ONE call for four previews, so it is a
// screen and not a component that could be dropped anywhere.
import { PlaygroundButtonsPage } from './pages/PlaygroundButtons'
import { LoginPage } from './pages/Login'
import { ForgotPasswordPage } from './pages/ForgotPassword'
import { ResetPasswordPage } from './pages/ResetPassword'
import { AcceptInvitePage } from './pages/AcceptInvite'
import { NotFoundPage } from './pages/NotFound'
import { TransfersPage } from './pages/Transfers'
// Owned by the call client. It holds the peer connection, the encoded-frame tap
// and the two sockets; this file knows only that it takes an agent id.
import { CallPage } from './pages/Call'

/** URL-safe, to the end of the path. See the note at the top of the file. */
const SESSION_PATH = /^\/sessions\/([A-Za-z0-9_.:-]{1,128})$/
const AGENT_PATH = /^\/agents\/([A-Za-z0-9_.:-]{1,128})$/
const TOOL_PATH = /^\/tools\/([A-Za-z0-9_.:-]{1,128})$/
const WEBHOOK_PATH = /^\/webhooks\/([A-Za-z0-9_.:-]{1,128})$/

export default function App() {
  // The provider is outside the router, not inside it: the session belongs to
  // the tab and must not be re-fetched because somebody clicked a link.
  return (
    <AuthProvider>
      <Panel />
    </AuthProvider>
  )
}

function Panel() {
  const route = useRoute()
  const { user, loading, signOut } = useAuth()
  const [agents, setAgents] = useState<AgentConfig[] | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [sessionCounts, setSessionCounts] = useState<{ sessions: number; live: number } | null>(
    null,
  )

  const path = route.split('?')[0] ?? '/'
  const search = route.includes('?') ? route.slice(route.indexOf('?') + 1) : ''
  const params = new URLSearchParams(search)

  useEffect(() => {
    if (!user) {
      // Dropped rather than kept. See the file header: the next person to sign
      // in is not necessarily allowed to see this list.
      setAgents(null)
      setApiError(null)
      return
    }
    const ac = new AbortController()
    agentsApi
      .list(ac.signal)
      .then((res) => {
        setAgents(res.rows)
        setApiError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        // ONLY A TRANSPORT OR SERVER FAILURE EARNS THE SHELL BANNER. A 4xx on
        // this endpoint means the app is talking to something it does not
        // understand, which the screens report themselves; a network error or a
        // 5xx means nothing below is trustworthy, and that is the one condition
        // the banner exists for.
        const fatal = !(e instanceof ApiError) || e.status >= 500
        if (fatal) setApiError(e instanceof Error ? e.message : String(e))
      })
    return () => ac.abort()
    // The id, not the object: `refresh()` mints a new user object for the same
    // person on every login and role change, and none of those change which
    // agents exist.
  }, [user?.id])

  // The one route that moves rather than substituting. An effect and not a
  // render-time navigate, because pushing history during a render is a side
  // effect in the middle of one.
  useEffect(() => {
    if (user && path === '/login') navigate('/')
  }, [user, path])

  // ------------------------------------------------------------------ public
  // Before the session is known, and regardless of it. See the file header.
  if (path === '/forgot-password') return <ForgotPasswordPage />
  if (path === '/reset-password') return <ResetPasswordPage token={params.get('token')} />
  if (path === '/accept-invite') return <AcceptInvitePage token={params.get('token')} />

  // -------------------------------------------------------------------- gate
  if (loading) return <AuthSplash />
  // Signed in and standing on /login: the effect above is already moving us, and
  // the splash covers the one frame before it lands.
  if (user && path === '/login') return <AuthSplash />
  if (!user) return <LoginPage wanted={path === '/login' ? undefined : path} />

  const canWrite = can(user, 'write')
  const sessionMatch = SESSION_PATH.exec(path)
  const agentMatch = AGENT_PATH.exec(path)
  const toolMatch = TOOL_PATH.exec(path)
  const webhookMatch = WEBHOOK_PATH.exec(path)

  // `?agent=` wins; otherwise the first configured agent. Undefined until the
  // list lands, which is why /call renders a waiting line rather than mounting
  // the call client with an empty id — creating a session against no agent is a
  // 400 the reader would have to interpret.
  const requestedAgent = params.get('agent')
  const defaultAgentId = requestedAgent ?? agents?.[0]?.id
  const counts: NavCounts = {
    sessions: sessionCounts?.sessions,
    live: sessionCounts?.live,
    agents: agents?.length,
  }

  return (
    <AppShell
      path={path}
      counts={counts}
      apiError={apiError}
      user={user}
      onSignOut={signOut}
    >
      {path === '/' ? (
        <SessionsPage onCounts={setSessionCounts} />
      ) : sessionMatch ? (
        <SessionDetailPage id={sessionMatch[1]!} />
      ) : path === '/agents' ? (
        <AgentsPage onRows={setAgents} />
      ) : agentMatch ? (
        <AgentEditPage id={agentMatch[1]!} />
      ) : toolMatch ? (
        <ToolEditPage id={toolMatch[1]!} />
      ) : path === '/tools' ? (
        // Like /users: readable by every role — the screens drop their own
        // write controls — so no viewer branch here.
        <ToolsPage />
      ) : webhookMatch ? (
        <WebhookEditPage id={webhookMatch[1]!} />
      ) : path === '/webhooks' ? (
        <WebhooksPage />
      ) : path === '/transfers' ? (
        // Readable by every role, like /tools and /webhooks: the screen drops its
        // own write controls. One route, no detail page — a transfer target is
        // five fields, so the list edits in place (see the page's header).
        <TransfersPage />
      ) : path === '/telephony' ? (
        // Readable by every role, like /tools: the credentials section shows
        // its state to non-admins and the screen drops its own write controls.
        <TelephonyPage />
      ) : path === '/users' ? (
        <UsersPage />
      ) : path === '/account' ? (
        <AccountPage />
      ) : path === '/playground/buttons' ? (
        // No viewer branch here, unlike /call. Every write on that screen is
        // absent for a viewer rather than disabled, and what is left — four
        // previews and their config forms — is worth reading, so the screen
        // renders for every role and drops its own controls.
        <PlaygroundButtonsPage agents={agents} />
      ) : path === '/call' ? (
        // The nav item is already hidden for a viewer, but a typed URL or an old
        // bookmark still arrives here. Saying why is cheaper for everybody than
        // a `read_only` error box from the session the screen would try to open.
        !canWrite ? (
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">Calls are read-only for you</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Starting a call creates a session, which the{' '}
              <span className="mono">{user.role}</span> role cannot do — it may read every
              screen here and change nothing. The record of calls other people made is open to
              you.{' '}
              <Link to="/" className="underline underline-offset-4">
                Sessions
              </Link>
            </p>
          </div>
        ) : defaultAgentId ? (
          <CallPage agentId={defaultAgentId} agents={agents} />
        ) : agents === null ? (
          <p className="loading">Loading agents…</p>
        ) : (
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">No agent to call</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              A session cannot be created without an agent, and the{' '}
              <span className="mono">agents</span> table has no rows. Seed one, then come
              back.{' '}
              <Link to="/agents" className="underline underline-offset-4">
                Agents
              </Link>
            </p>
          </div>
        )
      ) : (
        <NotFoundPage path={path} />
      )}
    </AppShell>
  )
}
